import { useMemo } from "react"
import { useTheme, type Theme } from "./ThemeProvider"

export type PanelTone = {
  background: string
  border: string
  shadow: string
  heading: string
  muted: string
}

export type PanelInset = {
  background: string
  border: string
}

export type OverlayTokens = {
  backdrop: string
  surface: PanelTone
  inputBackground: string
  inputBorder: string
  inputColor: string
}

export type TableTokens = {
  frame: string
  head: string
  body: string
  hover: string
  active: string
  border: string
}

export type ButtonTone = {
  background: string
  border: string
  color: string
  shadow: string
}

export type ThemeSurfaces = {
  panel: {
    primary: PanelTone
    secondary: PanelTone
    tertiary: PanelTone
    inset: PanelInset
    subtle: string
  }
  overlay: OverlayTokens
  table: TableTokens
  input: {
    background: string
    border: string
    color: string
    placeholder: string
  }
  button: {
    outline: ButtonTone
    outlineStrong: ButtonTone
    solid: ButtonTone
  }
  text: {
    heading: string
    muted: string
  }
  highlight: {
    soft: string
    pill: string
  }
  border: {
    dashed: string
    divider: string
  }
}

const SURFACE_MAP: Record<Theme, ThemeSurfaces> = {
  demon: {
    panel: {
      primary: {
        background: "rgba(18,18,20,0.85)",
        border: "1px solid rgba(255,43,79,0.45)",
        shadow: "0 18px 30px rgba(255,43,79,0.18)",
        heading: "var(--text)",
        muted: "var(--muted)",
      },
      secondary: {
        background: "rgba(18,18,20,0.75)",
        border: "1px solid rgba(255,255,255,0.18)",
        shadow: "0 12px 24px rgba(0,0,0,0.45)",
        heading: "var(--text)",
        muted: "var(--muted)",
      },
      tertiary: {
        background: "rgba(18,18,20,0.45)",
        border: "1px solid rgba(255,255,255,0.16)",
        shadow: "none",
        heading: "var(--text)",
        muted: "var(--muted)",
      },
      inset: {
        background: "rgba(18,18,20,0.35)",
        border: "1px solid rgba(255,255,255,0.18)",
      },
      subtle: "rgba(18,18,20,0.28)",
    },
    overlay: {
      backdrop: "rgba(0,0,0,0.6)",
      surface: {
        background: "rgba(18,18,20,0.92)",
        border: "1px solid rgba(255,255,255,0.18)",
        shadow: "0 18px 30px rgba(255,43,79,0.25)",
        heading: "var(--text)",
        muted: "var(--muted)",
      },
      inputBackground: "rgba(18,18,20,0.35)",
      inputBorder: "1px solid rgba(255,255,255,0.24)",
      inputColor: "var(--text)",
    },
    table: {
      frame: "rgba(18,18,20,0.75)",
      head: "rgba(18,18,20,0.85)",
      body: "rgba(12,12,14,0.8)",
      hover: "#13161b",
      active: "#13161b",
      border: "var(--rail)",
    },
    input: {
      background: "rgba(18,18,20,0.62)",
      border: "1px solid var(--rail)",
      color: "var(--text)",
      placeholder: "var(--muted)",
    },
    button: {
      outline: {
        background: "transparent",
        border: "2px solid rgba(255,43,79,0.45)",
        color: "var(--text)",
        shadow: "none",
      },
      outlineStrong: {
        background: "transparent",
        border: "2px solid var(--rail)",
        color: "var(--text)",
        shadow: "none",
      },
      solid: {
        background: "var(--rail)",
        border: "2px solid var(--rail)",
        color: "#0a0a0b",
        shadow: "none",
      },
    },
    text: {
      heading: "var(--text)",
      muted: "var(--muted)",
    },
    highlight: {
      soft: "rgba(255,43,79,0.12)",
      pill: "rgba(255,43,79,0.2)",
    },
    border: {
      dashed: "1px dashed rgba(255,43,79,0.45)",
      divider: "1px dashed rgba(255,255,255,0.18)",
    },
  },
  fairy: {
    panel: {
      primary: {
        background: "rgba(255,255,255,0.96)",
        border: "1px solid rgba(46,214,181,0.35)",
        shadow: "0 18px 30px rgba(46,214,181,0.12)",
        heading: "#1f2d42",
        muted: "#4b5a6d",
      },
      secondary: {
        background: "rgba(255,255,255,0.9)",
        border: "1px solid rgba(27,58,89,0.12)",
        shadow: "0 16px 28px rgba(46,214,181,0.1)",
        heading: "#1f2d42",
        muted: "#4b5a6d",
      },
      tertiary: {
        background: "rgba(244,255,251,0.74)",
        border: "1px solid rgba(27,58,89,0.16)",
        shadow: "none",
        heading: "#1f2d42",
        muted: "#4b5a6d",
      },
      inset: {
        background: "rgba(244,255,251,0.82)",
        border: "1px solid rgba(46,214,181,0.26)",
      },
      subtle: "rgba(211,246,239,0.65)",
    },
    overlay: {
      backdrop: "rgba(20,27,36,0.35)",
      surface: {
        background: "rgba(255,255,255,0.98)",
        border: "1px solid rgba(46,214,181,0.3)",
        shadow: "0 18px 36px rgba(46,214,181,0.18)",
        heading: "#1f2d42",
        muted: "#4b5a6d",
      },
      inputBackground: "rgba(244,255,251,0.9)",
      inputBorder: "1px solid rgba(46,214,181,0.3)",
      inputColor: "#1f2d42",
    },
    table: {
      frame: "rgba(255,255,255,0.9)",
      head: "rgba(244,255,251,0.9)",
      body: "rgba(236,252,245,0.86)",
      hover: "rgba(211,246,239,0.82)",
      active: "rgba(190,240,227,0.9)",
      border: "rgba(46,214,181,0.35)",
    },
    input: {
      background: "rgba(244,255,251,0.92)",
      border: "1px solid rgba(46,214,181,0.35)",
      color: "#1f2d42",
      placeholder: "#4b5a6d",
    },
    button: {
      outline: {
        background: "rgba(244,255,251,0.95)",
        border: "2px solid rgba(46,214,181,0.35)",
        color: "#1f2d42",
        shadow: "0 10px 24px rgba(46,214,181,0.18)",
      },
      outlineStrong: {
        background: "rgba(255,214,226,0.45)",
        border: "2px solid rgba(255,43,79,0.32)",
        color: "#5c1f2d",
        shadow: "0 10px 24px rgba(252,165,179,0.18)",
      },
      solid: {
        background: "rgba(244,255,251,0.95)",
        border: "2px solid var(--rail)",
        color: "#1f2d42",
        shadow: "0 10px 24px rgba(46,214,181,0.18)",
      },
    },
    text: {
      heading: "#1f2d42",
      muted: "#4b5a6d",
    },
    highlight: {
      soft: "rgba(46,214,181,0.14)",
      pill: "rgba(46,214,181,0.28)",
    },
    border: {
      dashed: "1px dashed rgba(46,214,181,0.35)",
      divider: "1px dashed rgba(27,58,89,0.18)",
    },
  },
}

export function useThemeSurfaces() {
  const { theme } = useTheme()

  return useMemo(() => SURFACE_MAP[theme], [theme])
}
